import { ActionItem } from '@urbanmix-tech/shared-js';

import {
  ButtonType,
  ConfirmButtons,
} from '@/components/shared/ConfirmButtons/ConfirmButtons';

import Markdown from 'react-markdown';
import { saveActionItem } from '@/data';

import { useState } from 'react';
import { useAuth } from '@/shared/context/AuthProvider';
import { convertPseudoDateToRealDate } from '@/helpers/common';
import { ModalContent } from '../../containers/ModalContent';
import { useLoading } from '@/shared/context/UIContextProvider';
import { useAlert } from '@/contexts/Alert/AlertContext';
import { TaskCommentsSection } from '@/components/BuildingTabs/TaskCommentsSection';
import { validateAndEnrich } from '@/components/JsonForms';
import { InspectionScopeContent } from '../InspectScopeContent';
import { WorkDoneContent } from '../WorkDoneContent';
import { ReviewWorkContent } from '../ReviewWorkContent';

const { VITE_API_SERVER, VITE_WORKFLOW_SECRET } = import.meta.env;

// Scope categories for inspection tasks
export const SCOPE_CATEGORIES = [
  { label: 'Windows', value: 'windows' },
  { label: 'Plumbing', value: 'plumbing' },
  { label: 'Safety/Pest', value: 'safety-pest' },
  { label: 'Appliances', value: 'appliances' },
  { label: 'Flooring', value: 'flooring' },
  { label: 'HVAC', value: 'hvac' },
  { label: 'Electrical', value: 'electrical' },
  { label: 'Other', value: 'other' },
] as const;

export type ScopeCategory = typeof SCOPE_CATEGORIES[number]['value'];

export type ActionItemModalProps = {
  actionItem: ActionItem;
  onChangeModalOpen: (val: boolean) => void;
};

export function ActionItemModal({
  actionItem,
  onChangeModalOpen,
}: ActionItemModalProps) {
  const [formContent, setFormContent] = useState<any>(actionItem.response);
  const [formError, setFormError] = useState<any>(undefined);

  const cancelAction: ButtonType = {
    label: 'Cancel',
    hidden: true,
    onClick: () => {
      onChangeModalOpen(false);
    },
  };

  const auth = useAuth();
  const usersData = auth.usersData;

  let owner = '-';
  let supervisor = '-';
  let followers: string[] = [];
  if (actionItem.owner?.userId) {
    owner = usersData[actionItem.owner?.userId]?.fullName ?? '-';
  }
  if (actionItem.supervisor?.userId) {
    supervisor = usersData[actionItem.supervisor?.userId]?.fullName ?? '-';
  }

  if (actionItem.followers && actionItem.followers.length > 0) {
    followers = actionItem.followers.map(
      (f) => usersData[f.userId]?.fullName ?? '-'
    );
  }
  const { createLoading } = useLoading();
  const { showAlert } = useAlert();

  const userAllowed =
    auth.userData?.isDev ||
    (!actionItem.owner?.userId && !actionItem.supervisor?.userId
      ? true
      : auth.user?.uid == actionItem.owner?.userId ||
        auth.user?.uid == actionItem.supervisor?.userId ||
        false);
  

  const executeWorkflow = async (actionItem : ActionItem,url : string) => {
    const loader = createLoading();
    loader.startLoading({title:"Executing workflow"});
    try {
      const result = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(VITE_WORKFLOW_SECRET && { 'X-Workflow-Secret': VITE_WORKFLOW_SECRET }),
        },
        body: JSON.stringify({
          actionItem,
          user: auth.userData?.email,
          userId: auth.userData?.id,
          userName: auth.userData?.fullName,
          "um-server": encodeURIComponent(VITE_API_SERVER),
        }),
      });
      console.log('result', result);
      if (!result.ok) {
        const errorText = await result.text().catch(() => result.statusText);
        throw new Error(`Workflow failed (${result.status}): ${errorText}`);
      }
    } catch (error) {
      console.error('Error executing workflow', error);
      showAlert("Error executing workflow", "error");
      throw error;
    } finally {
      loader.stopLoading();
    }
  }

  // console.log('modal save button',actionItem.response, actionItem.form_uischeme);
  const saveButton = (actionItem.response && Object.keys(actionItem.response).length > 0) || (actionItem.form_uischeme && Object(actionItem.form_uischeme).keys.length > 0) ? ({
    label: 'Save',
    onClick: async () => {
      let updatedItem = { ...actionItem };
      // set the status of the action item to 'done' and save any form responses
      if (updatedItem.response || updatedItem.form_scheme || formContent) {
        let currentResponse = updatedItem.response || {};
        updatedItem.response = {...currentResponse,...formContent};
      }
      // Clean the item to ensure no non-serializable values
      const cleanedItem = JSON.parse(JSON.stringify(updatedItem));
      const result = await saveActionItem(cleanedItem);
      if (result === null) {
        showAlert('Failed to save task. Please try again.', 'error');
        return;
      }
      showAlert('Task saved successfully', 'success');
      onChangeModalOpen(false);
    },
    disabled: !userAllowed,
  }) : undefined;
  // console.log('save button', saveButton);

  const confirmLabel = actionItem.action_type && actionItem.action_type.type == 'reminder' ? 'Complete' : 'Submit';

  const confirmButton = {
    label: confirmLabel,
    onClick: async () => {
      let updatedItem = { ...actionItem };
      // set the status of the action item to 'done' and save any form responses
      updatedItem.status = 'done';
      updatedItem.completedOn = new Date();
      if (updatedItem.response || updatedItem.form_scheme || formContent) {
        let currentResponse = updatedItem.response || {};
        updatedItem.response = {...currentResponse,...formContent};
      }
      // Clean the item to ensure no non-serializable values
      const cleanedItem = JSON.parse(JSON.stringify(updatedItem));

      try {
        const result = await saveActionItem(cleanedItem);
        if (result === null) {
          showAlert('Failed to save task. Please try again.', 'error');
          // Revert status since save failed
          updatedItem.status = actionItem.status;
          return;
        }
      } catch (error) {
        console.error('saveActionItem ERROR:', error);
        showAlert('Error saving task. Please try again.', 'error');
        return;
      }
      if (actionItem.interactive && actionItem.afterCompleteWorkflow) {
        try {
          await executeWorkflow(updatedItem, actionItem.afterCompleteWorkflow);
          await saveActionItem(updatedItem);
          onChangeModalOpen(false);
        } catch (error) {
          // workflow failed — revert status, keep modal open
          updatedItem.status = 'active';
          updatedItem.completedOn = undefined;
        }
      } else {
        await saveActionItem(updatedItem);
        onChangeModalOpen(false);
      }
    },
    disabled: formError
      ? formError.length > 0 || !userAllowed
      : !userAllowed,
    tooltip: !userAllowed
      ? 'Only the assigned person or a supervisor can complete this action'
      : formError && formError.length > 0
        ? `Please fill in all required fields\n${formError
            .map((e: any) => e.message)
            .join('\n')}`
        : undefined,
  }

  // The default acionts are approve and skip
  const defaultActions = (
    <ConfirmButtons
      alignItems="left"
      justifyContent='left'
      save={saveButton}
      confirm={confirmButton}
      cancel={cancelAction}
    />
  );

  const completedOnDate = actionItem.completedOn
    ? convertPseudoDateToRealDate(actionItem.completedOn)?.toString()
    : undefined;

  const isInspectCategory = actionItem.category === 'inspect';
  const isWorkCategory = actionItem.category === 'work' || actionItem.category === 'track';
  const isReviewCategory = actionItem.category === 'review';

  return (
    <ModalContent
      actionCategory={actionItem.category}
      onChangeModalOpen={onChangeModalOpen}
      headerTitle={actionItem.title}
      description={
        <Markdown className="popup-markdown">{actionItem.description}</Markdown>
      }
      owner={owner}
      supervisor={supervisor}
      followers={followers}
      moreInfo={completedOnDate}
      actionComponent={defaultActions}
      form_content={
        !isInspectCategory && 
        !isWorkCategory && 
        !isReviewCategory && 
        actionItem.response}
      form_scheme={
        !isInspectCategory && 
        !isWorkCategory && 
        !isReviewCategory && 
        actionItem.form_scheme}
      form_ui_scheme={
        !isInspectCategory && 
        !isWorkCategory && 
        !isReviewCategory && 
        actionItem.form_uischeme}
      on_content_change={({ data, errors }) => {
        const { enrichedData, customErrors } = validateAndEnrich(data, actionItem.form_scheme);
        // Only update state if data actually changed to prevent infinite loops
        if (JSON.stringify(enrichedData) !== JSON.stringify(formContent)) {
          setFormContent(enrichedData);
        }
        const combinedErrors = [...(errors || []), ...customErrors];
        if (JSON.stringify(combinedErrors) !== JSON.stringify(formError)) {
          setFormError(combinedErrors);
        }
      }}
      actionItem={actionItem}
      readonly={!userAllowed}
    >
      {/* Category-specific content */}
      {isInspectCategory && (
        <InspectionScopeContent
          actionItem={actionItem}
          onChange={(data) => setFormContent((prev: any) => ({ ...prev, ...data }))}
        />
      )}
      {isWorkCategory && (
        <WorkDoneContent
          actionItem={actionItem}
          onChange={(data) => setFormContent((prev: any) => ({ ...prev, ...data }))}
        />
      )}
      {isReviewCategory && (
        <ReviewWorkContent
          actionItem={actionItem}
          onChange={(data) => setFormContent((prev: any) => ({ ...prev, ...data }))}
        />
      )}
      <div className="mt-4">
        <TaskCommentsSection actionItem={actionItem} />
      </div>
    </ModalContent>
  );
}
